import { DocumentNode, gql } from '@apollo/client';
import {
  HStack,
  Image,
  LinkBox,
  LinkOverlay,
  Stack,
  Text,
} from '@chakra-ui/react';
import dayjs from 'dayjs';
import NextLink from 'next/link';
import { useRouter } from 'next/router';
import * as React from 'react';

import { EventScheduleInfo } from '@/components/EventScheduleInfo';
import { EventStuff } from '@/components/EventStuff';
import { ImagePreview } from '@/components/ImagePreview';
import { getAppropriateEventLink } from '@/hooks/getAppropriateEventLink';
import { usePermissions } from '@/hooks/usePermissions';
import { Card, CardProps } from '@/imports/ui/chakra/components/Card';
import { ImageFallbackMavericks } from '@/imports/ui/chakra/components/ImageFallbackMavericks';

import { PinEventButton } from './components/PinEventButton';

import { EventListItem_EventFragment } from './graphql-types/EventListItem_EventFragment';

export type EventListItemProps = {
  event: EventListItem_EventFragment;
  shadow?: CardProps['shadow'];

  // for analytics tracking purposes
  trackClickEvent?: () => void;
};

export const EventListItem: React.FC<EventListItemProps> & {
  eventFragment: DocumentNode;
} = (props) => {
  const router = useRouter();
  const permissions = usePermissions();
  const isEventPast = dayjs().isAfter(props.event.endDate);
  const link = getAppropriateEventLink(props.event);
  const showPin =
    permissions.canDoAdminStuffAndAuthenticated &&
    router.pathname === '/events' &&
    router.query.filter === 'upcoming' &&
    router.query.includeCurrentUserInvolvedEventsOnly !== 'true';
  const [xproEventURL, setXproEventURL] = React.useState<string | null>(null);
  const [xproEventBanner, setXproEventBanner] = React.useState<string | null>(null);
  React.useEffect(() => {
    if (props.event.__typename === 'SimpleEventV2' ) {
      if (props.event.isXproConferenceEvent && props.event.xproEventId) {
        const url =`${process.env.NEXT_PUBLIC_XPRO_REDIRECT_URL_GUEST}/${props.event.xproEventId}`;
        setXproEventURL(url);
        setXproEventBanner(props.event.xproEventBanner || null);
      } else {
        setXproEventURL(null);
        setXproEventBanner(null);
      }
    }
  }, [props.event]);
  const bannerSrc =
    props.event.bannerImage?.imageProxy || xproEventBanner || undefined;
  const bannerFallback = (
    <ImageFallbackMavericks
      borderTopRadius="xl"
      title={props.event.name}
    />
  );
  return (
    <LinkBox>
      <Card shadow={props.shadow}>
        <ImagePreview
          src={bannerSrc}
          alt={`${props.event.name} banner`}
          w="full"
          size="3xl"
          maxH="70vh"
          enableZoom
        >
          <Image
            borderTopRadius="xl"
            w="full"
            src={bannerSrc}
            alt={`${props.event.name} banner`}
            fallback={bannerFallback}
          />
        </ImagePreview>
        <Stack p="4" spacing="4">
          <Stack spacing="1">
            <HStack spacing="2" align="center">
              <LinkOverlay
                as={NextLink}
                href={xproEventURL ? '' : link}
                flex={showPin ? '1' : undefined}
                onClick={(e) => {
                  // XPRO user journey: always record the card click; XPro then opens the guest URL
                  props.trackClickEvent?.();
                  if (xproEventURL) {
                    e.preventDefault();
                    window.open(xproEventURL, '_blank');
                  }
                }}
              >
                <Text textStyle="xlBodyStrong">{props.event.name}</Text>
              </LinkOverlay>
              {showPin && <PinEventButton event={props.event} />}
            </HStack>

              <EventScheduleInfo
                event={props.event}
                showAddToCalendar={!isEventPast}
              />
          </Stack>

          <EventStuff
            event={props.event}
            allowSpecialTooltip={false}
            allowConferenceJoin={true}
          />
        </Stack>
      </Card>
    </LinkBox>
  );
};

EventListItem.eventFragment = gql`
  fragment EventListItem_EventFragment on EventV2 {
    _id
    endDate
    bannerImage {
      _id
      imageProxy(input: { ar: "8:3", w: 1600, fit: "fill", fill: "blur" })
    }
    ... on SimpleEventV2 {
      pinned
      isXproConferenceEvent
      xproEventId
      xproEventBanner
    }
    ... on ConferenceEventV2 {
      pinned
    }
    name
    ...GetAppropriateEventLink_EventFragment
    ...EventStuff_EventFragment
    ...EventScheduleInfo_EventFragment
  }
  ${getAppropriateEventLink.eventFragment}
  ${EventStuff.eventFragment}
  ${EventScheduleInfo.eventFragment}
`;
